/**
 * useFoodPreferencesAnalytics.js
 *
 * Analytics hook for the Assessment Wizard's Screen 7 ("Food
 * Preferences"). Mirrors useMedicalAnalytics.js / useLifestyleAnalytics.js
 * exactly — same GA4 wrapper via react-ga4, same IS_PRODUCTION guard, same
 * dev console.group/table logging.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[FoodPreferencesAnalytics] ${eventName}`, "color:#EF6C00;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

const useFoodPreferencesAnalytics = () => {
  const trackStepView = useCallback((sessionId) => {
    send("food_prefs_step_view", { session_id: sessionId, step: 7 });
  }, []);

  const trackDietTypeSelect = useCallback((sessionId, dietTypeId) => {
    send("food_prefs_diet_type_select", { session_id: sessionId, diet_type: dietTypeId });
  }, []);

  const trackCuisineToggle = useCallback((sessionId, cuisineId, selected) => {
    send("food_prefs_cuisine_toggle", {
      session_id: sessionId,
      cuisine: cuisineId,
      selected,
    });
  }, []);

  const trackCookingTimeSelect = useCallback((sessionId, cookingTimeId, filterMode) => {
    send("food_prefs_cooking_time_select", {
      session_id: sessionId,
      cooking_time: cookingTimeId,
      // "hard" | "soft" — FSD v2.1 §6. Logged so QA can confirm the
      // backend is treating this as a ranking factor, not a query
      // exclusion, unless explicitly reconfigured.
      filter_mode: filterMode,
    });
  }, []);

  const trackBudgetChange = useCallback((sessionId, budgetId, filterMode) => {
    send("food_prefs_budget_change", {
      session_id: sessionId,
      budget: budgetId,
      filter_mode: filterMode,
    });
  }, []);

  const trackStepNext = useCallback((sessionId, data = {}) => {
    send("food_prefs_step_next", {
      session_id: sessionId,
      step: 7,
      diet_type: data.dietType,
      cuisines: Array.isArray(data.cuisines) ? data.cuisines.join(",") : undefined,
      cooking_time: data.cookingTime,
      budget: data.budget,
      cooking_time_filter_mode: data.cookingTimeFilterMode,
      budget_filter_mode: data.budgetFilterMode,
    });
  }, []);

  const trackStepPrevious = useCallback((sessionId) => {
    send("food_prefs_step_previous", { session_id: sessionId, step: 7 });
  }, []);

  const trackValidationBlocked = useCallback((sessionId, errors = {}) => {
    send("food_prefs_validation_blocked", {
      session_id: sessionId,
      missing_fields: Object.keys(errors).join(","),
    });
  }, []);

  return {
    trackStepView,
    trackDietTypeSelect,
    trackCuisineToggle,
    trackCookingTimeSelect,
    trackBudgetChange,
    trackStepNext,
    trackStepPrevious,
    trackValidationBlocked,
  };
};

export default useFoodPreferencesAnalytics;